import React, { useState } from 'react';
import { CheckCircle2, XCircle, ChevronRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useAprovarPendingChange,
  usePendingChanges,
  useRejeitarPendingChange,
} from '../hooks';
import type { PendingChange, PendingChangeStatus } from '../api';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { Textarea } from '../../../shared/components/Textarea';

const FIELD_LABEL: Record<string, string> = {
  documento: 'Documento (CNPJ/CPF)',
  razaoSocial: 'Razão Social',
};

/**
 * Lista de alterações críticas aguardando aprovação (SPEC 02 §1 —
 * RN-COM-050, design D6). Restrito a perfis com
 * `client:approve-critical` — usuários sem a permissão veem "acesso
 * negado" em vez da lista.
 *
 * Cada linha mostra o diff (from → to) e permite aprovar/rejeitar
 * inline com motivo opcional.
 */
const AprovacoesPendentesPage: React.FC = () => {
  const { can } = usePermissions();
  const canApprove = can(FEATURES.clientesAprovacoes, 'edit');
  const [statusFilter, setStatusFilter] = useState<PendingChangeStatus>('pending');
  const { data, isLoading, isError, error } = usePendingChanges({ status: statusFilter });

  if (!canApprove) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Acesso restrito. Você não tem permissão <code>client:approve-critical</code>.
        </div>
      </div>
    );
  }

  const items = data?.data ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aprovações pendentes</h1>
          <p className="text-sm text-gray-500">
            Alterações críticas no cadastro de cliente (CNPJ, Razão Social, etc.) que aguardam
            aprovação (RN-COM-050).
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          {(['pending', 'approved', 'rejected'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`rounded-md px-3 py-1.5 ${
                statusFilter === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`filter-${s}`}
            >
              {s === 'pending' ? 'Pendentes' : s === 'approved' ? 'Aprovadas' : 'Rejeitadas'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando…</div>
      ) : isError ? (
        <div className="text-sm text-red-700">Erro: {(error as Error)?.message}</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nenhuma alteração {statusFilter === 'pending' ? 'pendente' : statusFilter === 'approved' ? 'aprovada' : 'rejeitada'} encontrada.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((p) => (
            <PendingChangeRow key={p.id} pending={p} canAct={statusFilter === 'pending'} />
          ))}
        </ul>
      )}
    </div>
  );
};

interface RowProps {
  pending: PendingChange;
  canAct: boolean;
}

const PendingChangeRow: React.FC<RowProps> = ({ pending, canAct }) => {
  const aprovar = useAprovarPendingChange();
  const rejeitar = useRejeitarPendingChange();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const busy = aprovar.isPending || rejeitar.isPending;

  const handleAprovar = async () => {
    setError(null);
    try {
      await aprovar.mutateAsync({ id: pending.id, reason: reason.trim() || undefined });
      setReason('');
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao aprovar.'));
    }
  };

  const handleRejeitar = async () => {
    setError(null);
    try {
      await rejeitar.mutateAsync({ id: pending.id, reason: reason.trim() || undefined });
      setReason('');
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao rejeitar.'));
    }
  };

  return (
    <li
      className="rounded-md border border-gray-200 bg-white p-4"
      data-testid={`pending-change-${pending.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <Link
              to={`/clientes/${pending.clientId}`}
              className="font-medium text-blue-600 hover:underline"
            >
              {pending.clientCode ?? pending.clientId}
              <ChevronRight size={12} className="inline" />
              {pending.clientRazaoSocial ?? '—'}
            </Link>
            <StatusBadge status={pending.status} />
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Solicitado em {new Date(pending.requestedAt).toLocaleString('pt-BR')}
            {pending.requestedByName ? ` por ${pending.requestedByName}` : ''}
          </div>
          <div className="mt-3 space-y-1">
            {Object.entries(pending.fieldChanges).map(([field, change]) => (
              <div key={field} className="text-sm" data-testid={`diff-${field}`}>
                <span className="font-medium text-gray-700">
                  {FIELD_LABEL[field] ?? field}:
                </span>{' '}
                <span className="rounded bg-red-50 px-1 text-red-700 line-through">
                  {formatValue(change.from)}
                </span>
                <span className="mx-1 text-gray-400">→</span>
                <span className="rounded bg-green-50 px-1 text-green-800">
                  {formatValue(change.to)}
                </span>
              </div>
            ))}
          </div>
          {pending.resolvedAt && (
            <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
              Resolvido em {new Date(pending.resolvedAt).toLocaleString('pt-BR')}
              {pending.resolvedByName ? ` por ${pending.resolvedByName}` : ''}
              {pending.resolutionReason ? ` — motivo: ${pending.resolutionReason}` : ''}
            </div>
          )}
        </div>
      </div>

      {canAct && (
        <div className="mt-4 space-y-2 border-t border-gray-100 pt-3">
          <Textarea
            label="Motivo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            data-testid={`motivo-${pending.id}`}
          />
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAprovar}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              data-testid={`btn-aprovar-${pending.id}`}
            >
              <CheckCircle2 size={14} /> Aprovar
            </button>
            <button
              type="button"
              onClick={handleRejeitar}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              data-testid={`btn-rejeitar-${pending.id}`}
            >
              <XCircle size={14} /> Rejeitar
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const StatusBadge: React.FC<{ status: PendingChangeStatus }> = ({ status }) => {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
        <Clock size={10} /> Pendente
      </span>
    );
  }
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        <CheckCircle2 size={10} /> Aprovada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
      <XCircle size={10} /> Rejeitada
    </span>
  );
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '(vazio)';
  if (typeof v === 'string') return v || '(vazio)';
  return JSON.stringify(v);
}

export default AprovacoesPendentesPage;
